// Core
import React from 'react';

// Plugins
import { capitalizeFirstLetter, formatNumber } from '../../plugins/general';

// Hooks
import { useOverflowTooltip } from '../../hooks/useOverflowTooltip/useOverflowTooltip';

// Components
import { Tooltip } from '../../layout/elements/Tooltip';

// Component
import { EquipmentSensorListItem, EquipmentSensorListProps } from './EquipmentSensorList.types';
import {
  Container,
  TitleContainer,
  ItemsLeftContainer,
  TitleLeftContainer,
  ListContainer,
  TitleRightContent,
  ItemsRightContainer,
  ItemsRightContent,
  TitleRightContainer,
  TooltipContent,
} from './EquipmentSensorList.style';

function TooltipItem({
  children,
  text,
}: { children: React.ReactNode; text: string }): JSX.Element {
  const { containerRef, contentRef, isTooltipVisible } = useOverflowTooltip({
    overflowCheckTriggers: text,
  });

  return isTooltipVisible ? (
    <Tooltip text={text}>
      <TooltipContent ref={containerRef}>
        <span ref={contentRef}>{children}</span>
      </TooltipContent>
    </Tooltip>
  ) : (
    <TooltipContent ref={containerRef}>
      <span ref={contentRef}>{children}</span>
    </TooltipContent>
  );
}

function EquipmentSensorList({
  className,
  items,
  testId,
}: EquipmentSensorListProps): JSX.Element {
  const columnsField: string[] = Object.keys(items[0]).filter((key) => key !== 'isBold');

  return (
    <Container
      className={className}
      data-testid={testId}
      dataSource={items}
      header={(
        <TitleContainer>
          <TitleLeftContainer>
            {capitalizeFirstLetter(columnsField[0])}
          </TitleLeftContainer>
          <TitleRightContainer>
            {columnsField.slice(1).map((field) => (
              <TitleRightContent key={field}>
                {capitalizeFirstLetter(field)}
              </TitleRightContent>
            ))}
          </TitleRightContainer>
        </TitleContainer>
      )}
      renderItem={(item: EquipmentSensorListItem): React.ReactNode => {
        const leftValue: React.ReactNode = item[columnsField[0]];
        const { isBold } = item;
        const { hasLargePadding } = item;

        return (
          <ListContainer>
            <ItemsLeftContainer isBold={isBold} hasLargePadding={hasLargePadding}>
              {typeof leftValue === 'number'
                ? formatNumber(leftValue)
                : leftValue}
            </ItemsLeftContainer>
            <ItemsRightContainer>
              {columnsField.slice(1).map((field: string): React.ReactNode => {
                const content = typeof item[field] === 'number'
                  ? formatNumber(item[field] as number)
                  : item[field];

                return (
                  <ItemsRightContent key={field}>
                    <TooltipItem text={String(content)}>
                      {content}
                    </TooltipItem>
                  </ItemsRightContent>
                );
              })}
            </ItemsRightContainer>
          </ListContainer>
        );
      }}
    />
  );
}

export { EquipmentSensorList };
